const ACTIVE_TENANT_KEY = "activeTenantId";
const TENANT_CATALOG_KEY = "tenantCatalog";
const SUBDOMAIN_TENANCY_ENABLED = String(
  process.env.REACT_APP_SUBDOMAIN_TENANCY_ENABLED ?? "true"
).toLowerCase() !== "false";
const PLATFORM_HOSTS = (process.env.REACT_APP_PLATFORM_HOSTS || "localhost,127.0.0.1,classnx.com,www.classnx.com")
  .split(",")
  .map((host) => host.trim().toLowerCase())
  .filter(Boolean);
const RESERVED_SUBDOMAINS = new Set(["www", "app", "api", "admin", "mail", "superadmin", "platform"]);
const LEGACY_DEMO_TENANT_IDS = new Set(["tenant-1", "tenant-2", "tenant-3"]);
const LEGACY_DEMO_TENANT_NAMES = new Set([
  "green valley school",
  "scholars public academy",
  "sunrise international",
]);

const SUBDOMAIN_TENANT_CACHE_PREFIX = "subdomain_tenant_";
const PAID_BRANDING_PLANS = new Set([
  "ONE_MONTH",
  "THREE_MONTH",
  "SIX_MONTH",
  "ONE_YEAR",
  "STARTER",
  "GROWTH",
  "PROFESSIONAL",
  "PREMIUM",
  "PRO",
  "ENTERPRISE",
]);

export const normalizeTenant = (tenant = {}, index = 0) => {
  const schoolName = String(tenant.schoolName || tenant.name || `Tenant ${index + 1}`);
  const logoUrl = String(tenant.logoUrl || tenant.schoolLogoUrl || "");
  const subdomain = String(tenant.subdomain || "");
  const isSubdomain = Boolean(subdomain);
  const brandingEnabled = tenant.brandingEnabled !== undefined
    ? Boolean(tenant.brandingEnabled)
    : (tenant.whiteLabelEnabled !== undefined ? Boolean(tenant.whiteLabelEnabled) : isSubdomain);

  return {
    id: String(tenant.id || tenant.tenantId || tenant._id || (subdomain ? `tenant-${subdomain}` : `tenant-${index + 1}`)),
    tenantId: String(tenant.tenantId || tenant.id || tenant._id || ""),
    name: schoolName,
    schoolName,
    schoolLogoUrl: logoUrl,
    logoUrl,
    faviconUrl: String(tenant.faviconUrl || ""),
    primaryColor: tenant.primaryColor || (isSubdomain ? "#4f46e5" : null),
    secondaryColor: tenant.secondaryColor || (isSubdomain ? "#7c3aed" : null),
    subdomain,
    brandingEnabled,
    whiteLabelEnabled: brandingEnabled,
    isSubdomainTenant: brandingEnabled && isSubdomain,
    subscription: tenant.subscription || null,
    // Plan data in this client-side catalog is display-only. Entitlements must
    // come from the authenticated subscription response, never this storage.
    plan: String(tenant.plan || ""),
  };
};

const isLegacyDemoTenant = (tenant = {}) => {
  const normalizedId = String(tenant.id || "").trim().toLowerCase();
  const normalizedName = String(tenant.name || "").trim().toLowerCase();
  return LEGACY_DEMO_TENANT_IDS.has(normalizedId) || LEGACY_DEMO_TENANT_NAMES.has(normalizedName);
};

const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const getDefaultTenants = () => [];

export const isSubdomainTenancyEnabled = () => SUBDOMAIN_TENANCY_ENABLED;

// Read tenant slug from URL search param if present (e.g. ?subdomain=fatowef for dev/testing)
export const getTenantSlugFromQuery = () => {
  if (typeof window === "undefined" || !window.location?.search) return "";
  try {
    const params = new URLSearchParams(window.location.search);
    const param = params.get("subdomain") || params.get("tenant") || "";
    return param.trim().toLowerCase();
  } catch {
    return "";
  }
};

// SUBDOMAIN TENANCY CHANGE: Read current browser hostname safely.
export const getCurrentHostname = () => {
  if (typeof window === "undefined") return "";
  return String(window.location.hostname || "").toLowerCase();
};

// SUBDOMAIN TENANCY CHANGE: Extract tenant slug from host like school1.your-domain.com.
export const getTenantSlugFromHostname = (hostname = getCurrentHostname()) => {
  let normalizedHost = String(hostname || "").toLowerCase().trim();
  if (normalizedHost.includes(":")) {
    normalizedHost = normalizedHost.split(":")[0];
  }
  const querySlug = getTenantSlugFromQuery();

  if (!normalizedHost) return querySlug;

  if (PLATFORM_HOSTS.includes(normalizedHost)) {
    return querySlug;
  }

  // Support local development with subdomains like fatowef.localhost
  if (normalizedHost.endsWith(".localhost")) {
    const parts = normalizedHost.split(".").filter(Boolean);
    if (parts.length >= 2 && !RESERVED_SUBDOMAINS.has(parts[0])) {
      return parts[0];
    }
    return querySlug;
  }

  const segments = normalizedHost.split(".").filter(Boolean);
  if (segments.length >= 3) {
    const slug = segments[0];
    if (slug && !RESERVED_SUBDOMAINS.has(slug)) {
      return slug;
    }
  }

  return querySlug;
};

// SUBDOMAIN TENANCY CHANGE: Frontend feature gate for subdomain tenant mode.
export const getSubdomainTenantContext = () => {
  const hostname = getCurrentHostname();
  if (!isSubdomainTenancyEnabled()) {
    return {
      hostname,
      slug: "",
      isSubdomainTenant: false,
      enabled: false,
    };
  }

  const slug = getTenantSlugFromHostname(hostname);
  return {
    hostname,
    slug,
    isSubdomainTenant: Boolean(slug),
    enabled: true,
  };
};

export const SUBDOMAIN_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

export const isValidSubdomainSlug = (slug = "") => {
  if (!slug || typeof slug !== "string") return false;
  const normalized = slug.trim().toLowerCase();
  if (normalized.length < 2 || normalized.length > 63) return false;
  if (RESERVED_SUBDOMAINS.has(normalized)) return false;
  return SUBDOMAIN_REGEX.test(normalized);
};

/**
 * Builds a secure tenant subdomain redirect URL if the user should be routed
 * to their dedicated school workspace (e.g., classnx.com -> abc.classnx.com/dashboard).
 * Returns null if the user is already on the correct subdomain, is a Super Admin,
 * or if the subdomain is invalid.
 */
export const buildTenantSubdomainUrl = (subdomain, path = "/", userRole = "") => {
  if (typeof window === "undefined") return null;

  // Super Admins operate globally across all tenants and must not be restricted
  // or redirected to a single tenant subdomain.
  const normalizedRole = String(userRole || "").toUpperCase().trim();
  if (normalizedRole === "ROLE_SUPER_ADMIN" || normalizedRole === "SUPER_ADMIN") {
    return null;
  }

  const slug = String(subdomain || "").trim().toLowerCase();
  if (!isValidSubdomainSlug(slug)) {
    return null;
  }

  const currentHost = getCurrentHostname();
  const currentSlug = getTenantSlugFromHostname(currentHost);

  // If already on the correct subdomain, no cross-host redirect is needed.
  if (currentSlug && currentSlug === slug) {
    return null;
  }

  // Sanitize path to prevent open redirect vulnerabilities
  let safePath = String(path || "/").trim();
  if (!safePath.startsWith("/") || safePath.startsWith("//") || safePath.includes("\\")) {
    safePath = `/${safePath.replace(/^[/\\*]+/, "")}`;
  }
  safePath = safePath.replace(/[\r\n\t]/g, "");

  const protocol = window.location.protocol || "https:";
  const port = window.location.port ? `:${window.location.port}` : "";

  // Local development support (e.g., localhost:3000 -> abc.localhost:3000)
  if (currentHost === "localhost" || currentHost === "127.0.0.1" || currentHost.endsWith(".localhost")) {
    return `${protocol}//${slug}.localhost${port}${safePath}`;
  }

  // Production or staging domain (e.g., classnx.com, www.classnx.com -> abc.classnx.com)
  const baseDomain = process.env.REACT_APP_ROOT_DOMAIN || "classnx.com";
  return `${protocol}//${slug}.${baseDomain}${port}${safePath}`;
};

export const getStoredSubdomainTenant = (slug) => {
  if (!slug) return null;
  const key = `${SUBDOMAIN_TENANT_CACHE_PREFIX}${slug}`;
  try {
    const stored = sessionStorage.getItem(key) || localStorage.getItem(key);
    return stored ? safeJsonParse(stored, null) : null;
  } catch {
    return null;
  }
};

export const persistSubdomainTenant = (slug, tenant) => {
  if (!slug || !tenant) return;
  const key = `${SUBDOMAIN_TENANT_CACHE_PREFIX}${slug}`;
  try {
    const data = JSON.stringify(tenant);
    sessionStorage.setItem(key, data);
    localStorage.setItem(key, data);
  } catch {
    // Ignore storage quota limits
  }
};

export const clearStoredSubdomainTenant = (slug) => {
  try {
    if (slug) {
      sessionStorage.removeItem(`${SUBDOMAIN_TENANT_CACHE_PREFIX}${slug}`);
      localStorage.removeItem(`${SUBDOMAIN_TENANT_CACHE_PREFIX}${slug}`);
    }
    if (typeof localStorage !== "undefined") {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(SUBDOMAIN_TENANT_CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
    if (typeof sessionStorage !== "undefined") {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(SUBDOMAIN_TENANT_CACHE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
    }
  } catch {
    // Ignore storage errors
  }
};

export const applyTenantBranding = (tenant = {}) => {
  if (typeof document === "undefined") return;

  if (tenant?.brandingEnabled === false) {
    resetTenantBranding();
    return;
  }

  const faviconUrl = tenant?.faviconUrl;
  if (faviconUrl) {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "shortcut icon";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }

  const root = document.documentElement;
  if (tenant?.primaryColor) {
    root.style.setProperty("--brand-primary", tenant.primaryColor);
    root.style.setProperty("--theme-brand-primary", tenant.primaryColor);
  } else {
    root.style.removeProperty("--brand-primary");
    root.style.removeProperty("--theme-brand-primary");
  }

  if (tenant?.secondaryColor) {
    root.style.setProperty("--brand-secondary", tenant.secondaryColor);
    root.style.setProperty("--theme-brand-secondary", tenant.secondaryColor);
  } else {
    root.style.removeProperty("--brand-secondary");
    root.style.removeProperty("--theme-brand-secondary");
  }

  if (tenant?.primaryColor || tenant?.secondaryColor) {
    const primary = tenant.primaryColor || "#1e40af";
    const secondary = tenant.secondaryColor || primary;
    root.style.setProperty("--brand-gradient", `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`);
  } else {
    root.style.removeProperty("--brand-gradient");
  }
};

export const resetTenantBranding = () => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.removeProperty("--brand-primary");
  root.style.removeProperty("--brand-secondary");
  root.style.removeProperty("--theme-brand-primary");
  root.style.removeProperty("--theme-brand-secondary");
  root.style.removeProperty("--brand-gradient");

  let link = document.querySelector("link[rel*='icon']");
  if (link) {
    link.href = `${process.env.PUBLIC_URL || ""}/favicon.ico?v=classnx-20261002`;
  }
};

export const getStoredTenantCatalog = () => {
  const stored = localStorage.getItem(TENANT_CATALOG_KEY);
  const parsed = stored ? safeJsonParse(stored, []) : [];

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return [];
  }

  return parsed.map(normalizeTenant).filter((tenant) => !isLegacyDemoTenant(tenant));
};

export const persistTenantCatalog = (tenants = []) => {
  const nextCatalog = (Array.isArray(tenants) ? tenants : [])
    .map(normalizeTenant)
    .filter((tenant) => !isLegacyDemoTenant(tenant));
  localStorage.setItem(TENANT_CATALOG_KEY, JSON.stringify(nextCatalog));
  return nextCatalog;
};

export const syncTenantCatalog = (tenants = []) => {
  return persistTenantCatalog(Array.isArray(tenants) ? tenants : []);
};

export const getStoredTenantId = () => {
  const storedTenantId = localStorage.getItem(ACTIVE_TENANT_KEY);
  if (storedTenantId) return storedTenantId;

  const catalog = getStoredTenantCatalog();
  return catalog[0]?.id || "";
};

export const setStoredTenantId = (tenantId) => {
  if (!tenantId) {
    localStorage.removeItem(ACTIVE_TENANT_KEY);
    return "";
  }

  localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
  return tenantId;
};

export const getStoredTenant = () => {
  const tenantId = getStoredTenantId();
  return getStoredTenantCatalog().find((tenant) => tenant.id === tenantId) || null;
};

export const ensureTenantSelection = () => {
  const catalog = getStoredTenantCatalog();
  const activeTenantId = getStoredTenantId();
  const selectedTenant = catalog.find((tenant) => tenant.id === activeTenantId);

  if (selectedTenant) return selectedTenant;

  const fallbackTenant = catalog[0] || null;
  if (fallbackTenant) {
    setStoredTenantId(fallbackTenant.id);
  }
  return fallbackTenant;
};

export const clearStoredTenantSelection = () => {
  localStorage.removeItem(ACTIVE_TENANT_KEY);
};

export const clearStoredTenantData = () => {
  localStorage.removeItem(ACTIVE_TENANT_KEY);
  localStorage.removeItem(TENANT_CATALOG_KEY);
  localStorage.removeItem("activeTenantName");
};

export const buildTenantFromAuthPayload = (payload = {}, existingSubdomainTenant = null) => {
  const tenantId = payload.tenantId || payload.schoolId || payload.organizationId || existingSubdomainTenant?.tenantId || existingSubdomainTenant?.id || "";
  const tenantName = payload.schoolName || payload.tenantName || payload.organizationName || existingSubdomainTenant?.schoolName || existingSubdomainTenant?.name || "";

  if (!tenantId && !tenantName) return null;

  const subdomain = payload.subdomain || existingSubdomainTenant?.subdomain || "";
  const hasPayloadLogo = Object.prototype.hasOwnProperty.call(payload, "schoolLogoUrl")
    || Object.prototype.hasOwnProperty.call(payload, "logoUrl");
  const logoUrl = hasPayloadLogo
    ? String(payload.schoolLogoUrl || payload.logoUrl || "")
    : (existingSubdomainTenant?.logoUrl || existingSubdomainTenant?.schoolLogoUrl || "");
  const faviconUrl = payload.faviconUrl || existingSubdomainTenant?.faviconUrl || "";
  const primaryColor = payload.primaryColor || existingSubdomainTenant?.primaryColor || null;
  const secondaryColor = payload.secondaryColor || existingSubdomainTenant?.secondaryColor || null;
  const plan = payload.plan || payload.subscriptionPlan || existingSubdomainTenant?.plan || "";
  const hasFlattenedSubscription = [
    "subscriptionStatus",
    "subscriptionActive",
    "subscriptionExpired",
    "subscriptionPlan",
    "subscriptionExpiryDate",
  ].some((key) => Object.prototype.hasOwnProperty.call(payload, key));
  const subscription = payload.subscription
    || payload.tenant?.subscription
    || (hasFlattenedSubscription ? {
      active: payload.subscriptionActive ?? payload.active,
      expired: payload.subscriptionExpired ?? payload.expired,
      status: payload.subscriptionStatus,
      plan,
      expiryDate: payload.subscriptionExpiryDate || payload.expiryDate,
      whiteLabelEnabled: payload.whiteLabelEnabled,
    } : null);
  const subscriptionData = subscription?.raw || subscription || {};
  const subscriptionPlan = String(subscriptionData.plan || subscriptionData.planName || plan || "").trim().toUpperCase();
  const isExplicitlyInactiveOrExpired = subscriptionData.active === false
    || subscriptionData.expired === true
    || ["EXPIRED", "CANCELLED", "PAST_DUE", "SUSPENDED"].includes(String(subscriptionData.status || "").toUpperCase());
  const hasActivePaidPlan = PAID_BRANDING_PLANS.has(subscriptionPlan) && !isExplicitlyInactiveOrExpired;
  const isFreeOrUnpaidPlan = ["FREE", "TRIAL", "FREE_TRIAL", "UNPAID"].includes(subscriptionPlan);
  const isUnpaidPlan = isFreeOrUnpaidPlan || isExplicitlyInactiveOrExpired;

  const explicitlyDisabled = payload.whiteLabelEnabled === false
    || payload.brandingEnabled === false
    || subscriptionData.whiteLabelEnabled === false
    || isUnpaidPlan;

  const brandingEnabled = explicitlyDisabled
    ? false
    : (payload.whiteLabelEnabled === true || payload.brandingEnabled === true)
      ? true
      : (hasActivePaidPlan
        || existingSubdomainTenant?.brandingEnabled === true
        || existingSubdomainTenant?.whiteLabelEnabled === true
        || (existingSubdomainTenant?.isSubdomainTenant === true && Boolean(existingSubdomainTenant?.logoUrl || existingSubdomainTenant?.schoolLogoUrl))
        || (Boolean(subdomain) && (Boolean(logoUrl) || !subscriptionPlan)));

  return normalizeTenant({
    id: tenantId || tenantName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
    tenantId: tenantId || "",
    name: tenantName || "",
    schoolName: tenantName || "",
    schoolLogoUrl: logoUrl,
    logoUrl,
    faviconUrl,
    primaryColor,
    secondaryColor,
    subdomain,
    brandingEnabled,
    whiteLabelEnabled: brandingEnabled,
    subscription,
    plan,
  });
};
